import { fileDownload } from 'utils/fileDownload';
import { logError } from 'utils/sentryUtils';

import { mmFetch } from './mmFetch';
import { mmFetchWithResponse } from './mmFetchWithResponse';

/**
 * Builds query params for the V2 purchase order search endpoint.
 * @param {Object} params - Search parameters
 * @returns {URLSearchParams}
 */
const ALLOWED_SORT_FIELDS = ['id', 'created_at', 'vendor_name'];
const ALLOWED_SORT_DIRS = ['asc', 'desc'];

const buildPurchaseOrderSearchParams = (params = {}) => {
    const queryParams = new URLSearchParams();

    // Pagination
    if (params.pageSize !== undefined) queryParams.set('pageSize', params.pageSize);
    if (params.cursor !== undefined) queryParams.set('cursor', params.cursor);
    if (params.totalCount !== undefined) queryParams.set('totalCount', params.totalCount);

    // Text filters
    if (params.poNumber) queryParams.set('poNumber', params.poNumber);
    if (params.specialInstructions) queryParams.set('specialInstructions', params.specialInstructions);

    // Date filters
    if (params.deliveryDateStart) queryParams.set('deliveryDateStart', params.deliveryDateStart);
    if (params.deliveryDateEnd) queryParams.set('deliveryDateEnd', params.deliveryDateEnd);
    if (params.createdAt) queryParams.set('createdAt', params.createdAt);

    // Multi-select array filters
    if (params.vendorIds?.length) {
        params.vendorIds.forEach((id) => queryParams.append('vendorIds[]', id));
    }
    if (params.facilityIds?.length) {
        params.facilityIds.forEach((id) => queryParams.append('facilityIds[]', id));
    }
    if (params.statuses?.length) {
        params.statuses.forEach((s) => queryParams.append('statuses[]', s));
    }
    if (params.externalSkus?.length) {
        params.externalSkus.forEach((sku) => queryParams.append('externalSkus[]', sku));
    }
    if (params.companySourceIds?.length) {
        params.companySourceIds.forEach((id) => queryParams.append('companySourceIds[]', id));
    }
    if (params.buyerIds?.length) {
        params.buyerIds.forEach((id) => queryParams.append('buyerIds[]', id));
    }
    if (params.assortmentCategories?.length) {
        params.assortmentCategories.forEach((c) => queryParams.append('assortmentCategories[]', c));
    }
    if (params.purchaseOrderTypes?.length) {
        params.purchaseOrderTypes.forEach((t) => queryParams.append('purchaseOrderTypes[]', t));
    }
    if (params.l1TaxonomyIds?.length) {
        params.l1TaxonomyIds.forEach((id) => queryParams.append('l1TaxonomyIds[]', id));
    }
    if (params.l2TaxonomyIds?.length) {
        params.l2TaxonomyIds.forEach((id) => queryParams.append('l2TaxonomyIds[]', id));
    }
    if (params.deliveryType) {
        queryParams.set('deliveryType', params.deliveryType);
    }

    // Boolean filters
    if (params.hasCrossDock !== undefined) {
        queryParams.set('hasCrossDock', String(params.hasCrossDock));
    }
    if (params.isPriority !== undefined) {
        queryParams.set('isPriority', String(params.isPriority));
    }
    if (params.hasAppointment !== undefined) {
        queryParams.set('hasAppointment', String(params.hasAppointment));
    }
    if (params.sentToVendor !== undefined) {
        queryParams.set('sentToVendor', String(params.sentToVendor));
    }

    // Sorting — only send both sortField and sortDir together, and only if valid
    const { sortField } = params;
    const { sortDir } = params;
    if (sortField && sortDir && ALLOWED_SORT_FIELDS.includes(sortField) && ALLOWED_SORT_DIRS.includes(sortDir)) {
        queryParams.set('sortField', sortField);
        queryParams.set('sortDir', sortDir);
    }

    return queryParams;
};

const procurementApi = {
    addItemToPurchaseOrder: async (id, item) => {
        const url = `api/procurement/v1/purchaseOrder/${id}/items`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
        return response;
    },
    cancelCreditRequest: async (id) => {
        const url = `api/procurement/v1/creditRequest/${id}/cancel`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    cancelPurchaseOrder: async (id, reasonCode) => {
        const url = `api/procurement/v1/purchaseOrder/${id}/cancel`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ reasonCode }),
        });
        return response;
    },
    cloneItemOnPO: async (poID, poMapID) => {
        const url = `api/procurement/v1/purchaseOrder/${poID}/item/${poMapID}/clone`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    receiveCreditRequest: async (id) => {
        const url = `api/procurement/v1/creditRequest/${id}/receive`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    confirmPurchaseOrdersUpload: async (key) => {
        const url = 'api/procurement/v1/purchaseOrders/confirm';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ key }),
        });
        return response;
    },
    createShipper: async (item) => {
        const url = `api/procurement/v1/shipper`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
        return response;
    },
    createPurchaseOrder: async (item) => {
        const url = `api/procurement/v1/purchaseOrder`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
        return response;
    },
    createCreditRequest: async (data) => {
        const url = `api/procurement/v1/creditRequest`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(data),
        });
        return response;
    },
    downloadPOCSV: async (poID, vendorName) => {
        const filename = `PO#${poID}-${vendorName}.csv`;
        const url = `api/procurement/v1/purchaseOrder/${poID}/csv`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
            }
        } catch (e) {
            logError(e);
        }
    },
    downloadPODocument: async (poID, documentID, docName) => {
        const url = `api/procurement/v1/purchaseOrder/${poID}/documents/${documentID}`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, docName);
            }
        } catch (e) {
            logError(e);
        }
    },
    downloadPOPDF: async (poID, vendorName, fcName) => {
        let filename;

        if (fcName !== undefined) {
            // MM PO
            filename = `${fcName}-PO#${poID}-${vendorName}.pdf`;
        } else {
            // FBM ASN
            filename = `PO#${poID}.pdf`;
        }
        const url = `api/procurement/v1/purchaseOrder/${poID}/pdf`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename, false);
            }
        } catch (e) {
            logError(e);
        }
    },
    downloadPOShipList: async (poID) => {
        const filename = `ship_list_${poID}.pdf`;
        const url = `api/procurement/v1/purchaseOrder/${poID}/shipList/pdf`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
            }
        } catch (e) {
            logError(e);
        }
    },
    downloadPOCSVWithStatus: async (poID, vendorName) => {
        const filename = `PO#${poID}-${vendorName}.csv`;
        const url = `api/procurement/v1/purchaseOrder/${poID}/csv`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
                return { status: true };
            }
            let errorMessage = 'Failed to download CSV.';
            try {
                const payload = await res.json();
                if (payload?.msg) {
                    errorMessage = payload.msg;
                }
            } catch (err) {
                // Ignore JSON parsing failures.
            }
            return { status: false, msg: errorMessage };
        } catch (e) {
            return { status: false, msg: 'Failed to download CSV.' };
        }
    },
    downloadPOPDFWithStatus: async (poID, vendorName, fcName) => {
        let filename;

        if (fcName !== undefined) {
            // MM PO
            filename = `${fcName}-PO#${poID}-${vendorName}.pdf`;
        } else {
            // FBM ASN
            filename = `PO#${poID}.pdf`;
        }
        const url = `api/procurement/v1/purchaseOrder/${poID}/pdf`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename, false);
                return { status: true };
            }
            let errorMessage = 'Failed to download PDF.';
            try {
                const payload = await res.json();
                if (payload?.msg) {
                    errorMessage = payload.msg;
                }
            } catch (err) {
                // Ignore JSON parsing failures.
            }
            return { status: false, msg: errorMessage };
        } catch (e) {
            return { status: false, msg: 'Failed to download PDF.' };
        }
    },
    downloadPOShipListWithStatus: async (poID) => {
        const filename = `ship_list_${poID}.pdf`;
        const url = `api/procurement/v1/purchaseOrder/${poID}/shipList/pdf`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
                return { status: true };
            }
            let errorMessage = 'Failed to download ship list.';
            try {
                const payload = await res.json();
                if (payload?.msg) {
                    errorMessage = payload.msg;
                }
            } catch (err) {
                // Ignore JSON parsing failures.
            }
            return { status: false, msg: errorMessage };
        } catch (e) {
            return { status: false, msg: 'Failed to download ship list.' };
        }
    },
    getBuyers: async () => {
        const url = `api/procurement/v1/buyers`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getCreditRequests: async () => {
        const url = `api/procurement/v1/creditRequest`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getCreditRequestItems: async (id) => {
        const url = `api/procurement/v1/creditRequest/${id}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getCreditRequestAccountingStatuses: async () => {
        const url = `api/procurement/v1/creditRequestStatuses`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getCreditRequestReasons: async () => {
        const url = `api/procurement/v1/creditRequestReasons`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPOsPlaced: async (facilityID = '', truckPoType = '') => {
        let url = `api/procurement/v1/purchaseOrder?status=PLACED`;
        if (facilityID) {
            url += `&facilityID=${facilityID}`;
        }
        if (truckPoType) {
            url += `&poType=${truckPoType}`;
        }
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getTruckAssignablePurchaseOrdersByFacility: async (facilityId) => {
        const url = `api/procurement/v1/purchase-orders/truck-assignable?facilityId=${facilityId}`;
        return mmFetchWithResponse(url);
    },
    getAllPOs: async (facilityID) => {
        const url = `api/procurement/v1/allPurchaseOrders?facilityID=${facilityID}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getProvinces: async (country) => {
        const url = `api/addresses/v1/provinces?country=${country}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPurchaseOrderDetails: async (id) => {
        const url = `api/procurement/v1/purchaseOrder/${id}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getLastPOItemFromVendor: async (vendorID, intSku) => {
        const url = `api/procurement/v1/search/purchaseOrders/items?vendorID=${vendorID}&intSKU=${intSku}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPurchaseOrderDocuments: async (id) => {
        const url = `api/procurement/v1/purchaseOrder/${id}/documents`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPurchaseOrderItemTypes: async () => {
        const url = `api/procurement/v1/purchaseOrder/items/types`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPurchaseOrders: async (facilityID = 0, externalSku = null, poID = null, typeFilter = null) => {
        let url = `api/procurement/v1/purchaseOrder?facilityID=${facilityID}`;
        if (externalSku) {
            url += `&externalSku=${externalSku}`;
        }
        if (poID) {
            url += `&poID=${poID}`;
        }
        if (typeFilter) {
            url += `&typeFilters=${typeFilter}`;
        }
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getPOUpdateReasonCodes: async () => {
        const url = `api/procurement/v1/purchaseOrder/reasonCodes`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getShippers: async (activeOnly = true) => {
        const url = `api/procurement/v1/shippers?active=${activeOnly}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getSourcingReasons: async () => mmFetchWithResponse('api/procurement/v1/sourcing/reasons'),
    getUnpurchasedOutcomes: async () => mmFetchWithResponse('api/procurement/v1/unpurchased-outcomes'),
    getVendors: async (activeOnly = true) => {
        const url = `api/procurement/v1/vendor?active=${activeOnly}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    getIncompleteVendors: async () => {
        const url = `api/procurement/v1/incompletevendors`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
    removeItemFromPO: async (id, poMapID, reasonCode) => {
        const url = `api/procurement/v1/purchaseOrder/${id}/item/${poMapID}/remove`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ reasonCode }),
        });
        return response;
    },
    removePODocument: async (poID, id) => {
        const url = `api/procurement/v1/purchaseOrder/${poID}/documents/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'DELETE',
        });
        return response;
    },
    sendPurchaseOrderToVendor: async (poID) => {
        const url = `api/procurement/v1/purchaseOrder/${poID}/send`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
        });
        return response;
    },
    /**
     * @deprecated Prefer `downloadPrePopulatedCsvV2` for new code — this variant swallows errors
     * and does not return a reliable `{ status, msg }` result.
     */
    downloadPrePopulatedCsv: async (poIDs) => {
        const url = `api/procurement/v1/purchaseOrders/downloadPrePopulatedCsv`;

        try {
            const res = await mmFetch(url, {
                method: 'PUT',
                body: JSON.stringify(poIDs),
            });
            if (res.ok === true) {
                await fileDownload(res, 'pre-populated-PO-template.csv');
            }
        } catch (e) {
            logError(e);
        }
    },
    /**
     * Same endpoint as `downloadPrePopulatedCsv`, but returns `{ status: true }` on success and
     * `{ status: false, msg }` on failure so callers can distinguish errors (e.g. React Query mutations).
     * Callers that pass a bare id array are wrapped as `{ poIDs }` — the API expects that shape.
     */
    downloadPrePopulatedCsvV2: async (poIDs) => {
        const url = `api/procurement/v1/purchaseOrders/downloadPrePopulatedCsv`;

        try {
            const res = await mmFetch(url, {
                method: 'PUT',
                body: JSON.stringify(Array.isArray(poIDs) ? { poIDs } : poIDs),
            });
            if (res.ok === true) {
                await fileDownload(res, 'pre-populated-PO-template.csv');
                return { status: true };
            }
            let errorMessage = 'Failed to download CSV.';
            try {
                const payload = await res.json();
                if (payload?.msg) {
                    errorMessage = payload.msg;
                }
            } catch (err) {
                // Ignore JSON parsing failures.
            }
            return { status: false, msg: errorMessage };
        } catch (e) {
            logError(e);
            return { status: false, msg: e?.message ?? 'Failed to download CSV.' };
        }
    },
    updateCreditRequest: async (id, data) => {
        const url = `api/procurement/v1/creditRequest/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        return response;
    },
    updateCreditRequestItem: async (creditRequestID, itemID, data) => {
        const url = `api/procurement/v1/creditRequest/${creditRequestID}/item/${itemID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        return response;
    },
    updatePurchaseOrder: async (id, item) => {
        const url = `api/procurement/v1/purchaseOrder/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    updatePurchaseOrderMap: async (poID, poMapID, updates) => {
        const url = `api/procurement/v1/purchaseOrder/${poID}/item/${poMapID}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
        return response;
    },
    updateShipper: async (id, item) => {
        const url = `api/procurement/v1/shipper/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    updateVendor: async (id, item) => {
        const url = `api/procurement/v1/vendor/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    updateVendorAddress: async (id, item) => {
        const url = `api/procurement/v1/vendorAddress/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(item),
        });
        return response;
    },
    uploadPODocument: async (id, data) => {
        const formData = new FormData();

        Object.keys(data).forEach((key) => {
            formData.append(key, data[key]);
        });

        const url = `api/procurement/v1/purchaseOrder/${id}/documents`;
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },
    uploadPurchaseOrders: async (data) => {
        const formData = new FormData();
        formData.append('csvFile', data.file);
        const url = 'api/procurement/v1/purchaseOrders/upload';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
        return response;
    },

    // ==================== V2 API Endpoints ====================

    /**
     * Search purchase orders with cursor-based pagination (V2).
     * @param {Object} params - Search parameters including filters and pagination
     * @param {Object} [options] - Fetch options (e.g., { signal } for AbortController)
     * @returns {Promise<{status: boolean, msg: string, data: {rows: Array, pagination: Object, deptLeadCheck: boolean}}>}
     */
    searchPurchaseOrdersV2: async (params = {}, options = {}) => {
        const queryParams = buildPurchaseOrderSearchParams(params);
        const url = `api/procurement/v2/purchaseOrders?${queryParams}`;
        const response = await mmFetchWithResponse(url, options);
        return response;
    },

    /**
     * Get available PO status options for filtering.
     * @returns {Promise<{status: boolean, msg: string, data: {statuses: Array<{value: string, label: string}>}}>}
     */
    getStatusOptions: async () => {
        const url = 'api/procurement/v2/purchaseOrders/filters/statuses';
        const response = await mmFetchWithResponse(url);
        return response;
    },

    /**
     * Search external SKUs for filter dropdown.
     * @param {string} [search=''] - Search term
     * @returns {Promise<{status: boolean, msg: string, data: {externalSkus: Array<{value: number, label: string}>}}>}
     */
    searchExternalSkus: async (search = '') => {
        const queryParams = new URLSearchParams();
        if (search) queryParams.set('search', search);
        const url = `api/procurement/v2/purchaseOrders/filters/externalSkus?${queryParams}`;
        const response = await mmFetchWithResponse(url);
        return response;
    },
};

export { procurementApi };
